/**
 * Provider order (docs/modules/web.md):
 *   runtime config (read at module load) → QueryClientProvider → AppErrorBoundary → IntlProvider (locale from
 *   the lottery + participant override, timeZone and currency from the lottery) → MotionProvider (lotteryCalm
 *   from the published presentation) → LiveRegionProvider → ToastProvider → RouterProvider.
 * The lottery is published by LotteryLayout through `lottery-store` so the providers above the router can read
 * it without the router knowing about them.
 */
import { useEffect, useMemo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, type createBrowserRouter } from 'react-router';
import { IntlProvider, LiveRegionProvider, MotionProvider, ToastProvider } from '@windrop/ui';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { lotteryCalm, useLocaleOverride } from './lib/energy';
import { applyDocumentLocale, negotiateLocale, useMessages } from './lib/i18n';
import { useActiveLottery } from './lib/lottery-store';

/** Locale negotiated from the lottery's locales, the device languages and the participant override. */
export function useNegotiatedLocale(): string {
  const lottery = useActiveLottery();
  const [override] = useLocaleOverride();
  const queryLang =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('lang') : null;
  const locales = lottery?.locales;
  const fallback = lottery?.locale;
  return useMemo(
    () => negotiateLocale(locales ?? (fallback ? [fallback] : ['en']), undefined, override ?? queryLang),
    [locales, fallback, override, queryLang],
  );
}

export function AppProviders({ children }: { children: ReactNode }) {
  const lottery = useActiveLottery();
  const locale = useNegotiatedLocale();
  const { messages } = useMessages(locale);
  useEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);
  return (
    <IntlProvider
      locale={locale}
      timeZone={lottery?.schedule.timezone ?? 'UTC'}
      currency={lottery?.currency}
      messages={messages}
    >
      <MotionProvider lotteryCalm={lotteryCalm(lottery)}>
        <LiveRegionProvider>
          <ToastProvider>{children}</ToastProvider>
        </LiveRegionProvider>
      </MotionProvider>
    </IntlProvider>
  );
}

export interface AppProps {
  router: ReturnType<typeof createBrowserRouter>;
  queryClient: QueryClient;
}

export function App({ router, queryClient }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <AppProviders>
          <RouterProvider router={router} />
        </AppProviders>
      </AppErrorBoundary>
    </QueryClientProvider>
  );
}
